// Scene objects for the 3D viewer, built from the data in data.js.
import * as THREE from "three";
import { bedDepthAt, currentAt, isothermDepths, lakeIdAt } from "./data.js";
import { LAKE_IDS, toWorld } from "./geo.js";
import { MAX_LEVELS, curtainMaterial, isothermSurfaceMaterial, sliceMaterial, volumeUniforms } from "./materials.js";

export function paletteTexture(stops) {
  const width = 256;
  const data = new Uint8Array(width * 4);
  for (let index = 0; index < width; index += 1) {
    const [r, g, b] = paletteColor(index / (width - 1), stops);
    data.set([r, g, b, 255], index * 4);
  }
  const texture = new THREE.DataTexture(data, width, 1, THREE.RGBAFormat);
  texture.magFilter = THREE.LinearFilter;
  texture.minFilter = THREE.LinearFilter;
  texture.wrapS = THREE.ClampToEdgeWrapping;
  texture.needsUpdate = true;
  return texture;
}

export function paletteColor(position, stops) {
  const clamped = Math.max(0, Math.min(1, Number.isFinite(position) ? position : 0));
  const upper = Math.max(1, stops.findIndex(([stop]) => clamped <= stop));
  const [highStop, high] = stops[upper];
  const [lowStop, low] = stops[upper - 1];
  const fraction = highStop === lowStop ? 0 : (clamped - lowStop) / (highStop - lowStop);
  return low.map((channel, index) => Math.round(channel + (high[index] - channel) * fraction));
}

export function bedTexture(field) {
  const data = new Uint16Array(field.rows * field.columns * 2);
  for (let index = 0; index < field.depth.length; index += 1) {
    data[index * 2] = THREE.DataUtils.toHalfFloat(field.depth[index]);
    data[index * 2 + 1] = THREE.DataUtils.toHalfFloat(field.id[index]);
  }
  const texture = new THREE.DataTexture(data, field.columns, field.rows, THREE.RGFormat, THREE.HalfFloatType);
  texture.magFilter = THREE.LinearFilter;
  texture.minFilter = THREE.LinearFilter;
  texture.needsUpdate = true;
  return texture;
}

// A blurred copy of the lake mask, so the land can glow softly around the shores.
export function haloTexture(field) {
  const scale = 4;
  const columns = Math.ceil(field.columns / scale);
  const rows = Math.ceil(field.rows / scale);
  let values = new Float32Array(columns * rows);
  for (let row = 0; row < field.rows; row += 1) {
    for (let column = 0; column < field.columns; column += 1) {
      if (field.depth[row * field.columns + column] > 0) values[Math.floor(row / scale) * columns + Math.floor(column / scale)] += 1 / (scale * scale);
    }
  }
  const radius = 3;
  const blur = (source, horizontal) => {
    const result = new Float32Array(source.length);
    for (let row = 0; row < rows; row += 1) {
      for (let column = 0; column < columns; column += 1) {
        let total = 0;
        for (let offset = -radius; offset <= radius; offset += 1) {
          const r = horizontal ? row : Math.min(rows - 1, Math.max(0, row + offset));
          const c = horizontal ? Math.min(columns - 1, Math.max(0, column + offset)) : column;
          total += source[r * columns + c];
        }
        result[row * columns + column] = total / (radius * 2 + 1);
      }
    }
    return result;
  };
  for (let pass = 0; pass < 2; pass += 1) values = blur(blur(values, true), false);
  const data = new Uint8Array(values.length);
  for (let index = 0; index < values.length; index += 1) data[index] = Math.round(Math.min(1, values[index] * 2.2) * 255);
  const texture = new THREE.DataTexture(data, columns, rows, THREE.RedFormat, THREE.UnsignedByteType);
  texture.magFilter = THREE.LinearFilter;
  texture.minFilter = THREE.LinearFilter;
  texture.unpackAlignment = 1;
  texture.needsUpdate = true;
  return texture;
}

// One lake's lakebed mesh. Land vertices sit just above the water line and
// the shader discards everything shallower than zero, so the shoreline is a
// smooth interpolated curve instead of grid steps.
export function terrainMesh(lake, materialInstance) {
  const vertexCount = lake.rows * lake.columns;
  const positions = new Float32Array(vertexCount * 3);
  const depths = new Float32Array(vertexCount);
  for (let row = 0; row < lake.rows; row += 1) {
    const latitude = lake.north - row * lake.cellLatitude;
    for (let column = 0; column < lake.columns; column += 1) {
      const index = row * lake.columns + column;
      const [x, z] = toWorld(latitude, lake.west + column * lake.cellLongitude);
      const depth = lake.depths[index];
      positions.set([x, -depth / 1000, z], index * 3);
      depths[index] = depth;
    }
  }
  const indices = [];
  for (let row = 0; row < lake.rows - 1; row += 1) {
    for (let column = 0; column < lake.columns - 1; column += 1) {
      const a = row * lake.columns + column;
      const b = a + 1;
      const c = a + lake.columns;
      const d = c + 1;
      if (depths[a] > 0 || depths[b] > 0 || depths[c] > 0 || depths[d] > 0) indices.push(a, c, b, b, c, d);
    }
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  geometry.setAttribute("aDepth", new THREE.BufferAttribute(depths, 1));
  geometry.setIndex(vertexCount > 65535 ? new THREE.Uint32BufferAttribute(indices, 1) : new THREE.Uint16BufferAttribute(indices, 1));
  geometry.computeVertexNormals();
  geometry.computeBoundingSphere();
  const mesh = new THREE.Mesh(geometry, materialInstance);
  mesh.name = `${lake.slug}-lakebed`;
  return mesh;
}

// A flat rectangle in the x-z plane covering a latitude/longitude box.
export function groundPlane(south, west, north, east, segments = 1) {
  const [x0, z0] = toWorld(north, west);
  const [x1, z1] = toWorld(south, east);
  const geometry = new THREE.PlaneGeometry(x1 - x0, z1 - z0, segments, segments);
  geometry.rotateX(-Math.PI / 2);
  geometry.translate((x0 + x1) / 2, 0, (z0 + z1) / 2);
  return geometry;
}

export function volumeTexture(volume) {
  const grid = volume.temperature;
  const data = new Uint16Array(grid.filled.length);
  for (let index = 0; index < data.length; index += 1) data[index] = THREE.DataUtils.toHalfFloat(grid.filled[index]);
  const texture = new THREE.Data3DTexture(data, grid.columns, grid.rows, volume.depths.length);
  texture.format = THREE.RedFormat;
  texture.type = THREE.HalfFloatType;
  texture.magFilter = THREE.LinearFilter;
  texture.minFilter = THREE.LinearFilter;
  texture.unpackAlignment = 1;
  texture.needsUpdate = true;
  return texture;
}

export function applyVolumeUniforms(uniforms, volume, texture, range) {
  const grid = volume.temperature;
  uniforms.uVolume.value = texture;
  uniforms.uVolumeGrid.value.set(grid.south, grid.west, grid.cellLatitude, grid.cellLongitude);
  uniforms.uVolumeSize.value.set(grid.columns, grid.rows, volume.depths.length);
  const depths = new Array(MAX_LEVELS).fill(volume.depths[volume.depths.length - 1] + 1);
  volume.depths.slice(0, MAX_LEVELS).forEach((depth, index) => { depths[index] = depth; });
  uniforms.uDepths.value = depths;
  uniforms.uLevels.value = Math.min(MAX_LEVELS, volume.depths.length);
  uniforms.uRange.value.set(range[0], range[1]);
}

// Depth of the target temperature as a texture over the temperature grid:
// red is depth (gaps widened by neighbours so edges interpolate cleanly),
// green is whether the water column reaches the target at all.
export function isothermTexture(volume, targetC, existing) {
  const grid = volume.temperature;
  const { depths, count } = isothermDepths(volume, targetC);
  const cells = grid.rows * grid.columns;
  let current = Float32Array.from(depths);
  for (let pass = 0; pass < 2; pass += 1) {
    const next = Float32Array.from(current);
    for (let row = 0; row < grid.rows; row += 1) {
      for (let column = 0; column < grid.columns; column += 1) {
        const cell = row * grid.columns + column;
        if (Number.isFinite(current[cell])) continue;
        let total = 0;
        let neighbours = 0;
        for (let dr = -1; dr <= 1; dr += 1) {
          for (let dc = -1; dc <= 1; dc += 1) {
            const r = row + dr;
            const c = column + dc;
            if (r < 0 || c < 0 || r >= grid.rows || c >= grid.columns) continue;
            const value = current[r * grid.columns + c];
            if (Number.isFinite(value)) { total += value; neighbours += 1; }
          }
        }
        if (neighbours) next[cell] = total / neighbours;
      }
    }
    current = next;
  }
  const data = existing?.image.data?.length === cells * 2 ? existing.image.data : new Uint16Array(cells * 2);
  for (let cell = 0; cell < cells; cell += 1) {
    data[cell * 2] = THREE.DataUtils.toHalfFloat(Number.isFinite(current[cell]) ? current[cell] : 0);
    data[cell * 2 + 1] = THREE.DataUtils.toHalfFloat(Number.isFinite(depths[cell]) ? 1 : 0);
  }
  const texture = existing?.image.data === data ? existing : new THREE.DataTexture(data, grid.columns, grid.rows, THREE.RGFormat, THREE.HalfFloatType);
  texture.magFilter = THREE.LinearFilter;
  texture.minFilter = THREE.LinearFilter;
  texture.needsUpdate = true;
  const found = Array.from(depths).filter(Number.isFinite).sort((a, b) => a - b);
  const percentile = (fraction) => (found.length ? found[Math.min(found.length - 1, Math.floor(found.length * fraction))] : NaN);
  return { texture, count, typicalLow: percentile(0.1), typicalHigh: percentile(0.9) };
}

// A vertical wall from the surface to below the deepest water along a line.
export function curtainGeometry(start, end, maxDepth) {
  const segments = 320;
  const positions = new Float32Array((segments + 1) * 2 * 3);
  const [ax, az] = toWorld(start[0], start[1]);
  const [bx, bz] = toWorld(end[0], end[1]);
  const bottom = -(maxDepth + 5) / 1000;
  for (let index = 0; index <= segments; index += 1) {
    const t = index / segments;
    const x = ax + (bx - ax) * t;
    const z = az + (bz - az) * t;
    positions.set([x, 0, z, x, bottom, z], index * 6);
  }
  const indices = [];
  for (let index = 0; index < segments; index += 1) {
    const a = index * 2;
    indices.push(a, a + 1, a + 2, a + 1, a + 3, a + 2);
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  geometry.setIndex(indices);
  geometry.computeBoundingSphere();
  return geometry;
}

// The lakebed profile drawn as a line under a cross-section.
export function sectionFloorLine(field, start, end) {
  const samples = 320;
  const positions = [];
  for (let index = 0; index <= samples; index += 1) {
    const t = index / samples;
    const latitude = start[0] + (end[0] - start[0]) * t;
    const longitude = start[1] + (end[1] - start[1]) * t;
    const depth = bedDepthAt(field, latitude, longitude);
    const [x, z] = toWorld(latitude, longitude);
    positions.push(x, -Math.max(0, depth) / 1000, z);
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
  return new THREE.Line(geometry, new THREE.LineBasicMaterial({ color: 0xe7f6fb, transparent: true, opacity: 0.85 }));
}

export function pinMarker(color) {
  const group = new THREE.Group();
  const stem = new THREE.Mesh(new THREE.CylinderGeometry(0.35, 0.35, 10, 8), new THREE.MeshBasicMaterial({ color }));
  stem.position.y = 5;
  const head = new THREE.Mesh(new THREE.SphereGeometry(1.6, 16, 12), new THREE.MeshBasicMaterial({ color }));
  head.position.y = 10.5;
  group.add(stem, head);
  return group;
}

// Vertical probe from the surface to the lakebed under the pointer.
export const PROBE_OPACITY = 0.9;

export function probe() {
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.Float32BufferAttribute([0, 0, 0, 0, -1, 0], 3));
  const line = new THREE.Line(geometry, new THREE.LineBasicMaterial({ color: 0xffffff, transparent: true, opacity: PROBE_OPACITY, depthTest: false }));
  line.renderOrder = 10;
  line.visible = false;
  return line;
}

const RECORD_SECONDS = 0.08;
const FLOW_COLOR_STOPS = {
  speed: [[0.92, 0.97, 1], [1, 0.8, 0.35]],
  cyan: [[0.72, 0.98, 1], [0.08, 0.72, 1]],
  blue: [[0.76, 0.9, 1], [0.2, 0.42, 1]],
  violet: [[0.95, 0.82, 1], [0.66, 0.28, 1]],
  green: [[0.84, 1, 0.78], [0.2, 0.8, 0.38]],
  white: [[1, 1, 1], [0.82, 0.9, 1]],
  black: [[0.28, 0.28, 0.28], [0.03, 0.03, 0.03]]
};

function flowPalette(name) {
  const [slowRgb, fastRgb] = FLOW_COLOR_STOPS[name] || FLOW_COLOR_STOPS.speed;
  const slow = new THREE.Color().setRGB(...slowRgb, THREE.SRGBColorSpace);
  const fast = new THREE.Color().setRGB(...fastRgb, THREE.SRGBColorSpace);
  return Array.from({ length: 64 }, (_, index) => slow.clone().lerp(fast, index / 63));
}

// Flow particles that follow the modelled currents at one depth, leaving
// fading trails coloured by speed.
export class FlowParticles {
  constructor(count = 2200, trail = 12) {
    this.count = count;
    this.capacity = count;
    this.trail = trail;
    this.field = null;
    this.bed = null;
    this.wet = [];
    this.spawnWet = [];
    this.spawnBounds = null;
    this.depth = 0;
    this.positions = new Float32Array(count * trail * 2 * 3);
    this.colors = new Float32Array(count * trail * 2 * 4);
    this.dotPositions = new Float32Array(count * 3);
    this.dotColors = new Float32Array(count * 4);
    this.history = new Float32Array(count * trail * 2);
    this.age = new Float32Array(count);
    this.sinceRecord = 0;
    this.acceleration = 40000;
    this.life = new Float32Array(count);
    this.speed = new Float32Array(count);
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute("position", new THREE.BufferAttribute(this.positions, 3).setUsage(THREE.DynamicDrawUsage));
    geometry.setAttribute("color", new THREE.BufferAttribute(this.colors, 4).setUsage(THREE.DynamicDrawUsage));
    this.object = new THREE.LineSegments(geometry, new THREE.LineBasicMaterial({ vertexColors: true, transparent: true, depthWrite: false }));
    this.object.frustumCulled = false;
    this.object.renderOrder = 4;
    const dotMaterial = new THREE.ShaderMaterial({
      uniforms: { uPixelRatio: { value: 1 } },
      vertexShader: `
        uniform float uPixelRatio;
        attribute vec4 color;
        varying vec4 vColor;
        #include <clipping_planes_pars_vertex>
        void main() {
          vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
          gl_Position = projectionMatrix * mvPosition;
          gl_PointSize = 3.0 * uPixelRatio;
          #include <clipping_planes_vertex>
          vColor = color;
        }
      `,
      fragmentShader: `
        varying vec4 vColor;
        #include <clipping_planes_pars_fragment>
        void main() {
          #include <clipping_planes_fragment>
          float radius = length(gl_PointCoord - vec2(0.5));
          if (radius > 0.5) discard;
          float edgeAlpha = 1.0 - smoothstep(0.38, 0.5, radius);
          gl_FragColor = vec4(vColor.rgb, vColor.a * edgeAlpha);
          #include <tonemapping_fragment>
          #include <colorspace_fragment>
        }
      `,
      transparent: true,
      depthWrite: false,
      clipping: true
    });
    const dotGeometry = new THREE.BufferGeometry();
    dotGeometry.setAttribute("position", new THREE.BufferAttribute(this.dotPositions, 3).setUsage(THREE.DynamicDrawUsage));
    dotGeometry.setAttribute("color", new THREE.BufferAttribute(this.dotColors, 4).setUsage(THREE.DynamicDrawUsage));
    this.dots = new THREE.Points(dotGeometry, dotMaterial);
    this.dots.frustumCulled = false;
    this.dots.renderOrder = 5;
    this.paletteName = "speed";
    this.palette = flowPalette(this.paletteName);
  }

  setCount(count) {
    const nextCount = Math.max(1, Math.round(count));
    if (nextCount === this.count) return;
    if (nextCount > this.capacity) {
      this.capacity = nextCount;
      this.positions = new Float32Array(this.capacity * this.trail * 2 * 3);
      this.colors = new Float32Array(this.capacity * this.trail * 2 * 4);
      this.dotPositions = new Float32Array(this.capacity * 3);
      this.dotColors = new Float32Array(this.capacity * 4);
      this.history = new Float32Array(this.capacity * this.trail * 2);
      this.age = new Float32Array(this.capacity);
      this.life = new Float32Array(this.capacity);
      this.speed = new Float32Array(this.capacity);
      this.object.geometry.setAttribute("position", new THREE.BufferAttribute(this.positions, 3).setUsage(THREE.DynamicDrawUsage));
      this.object.geometry.setAttribute("color", new THREE.BufferAttribute(this.colors, 4).setUsage(THREE.DynamicDrawUsage));
      this.dots.geometry.setAttribute("position", new THREE.BufferAttribute(this.dotPositions, 3).setUsage(THREE.DynamicDrawUsage));
      this.dots.geometry.setAttribute("color", new THREE.BufferAttribute(this.dotColors, 4).setUsage(THREE.DynamicDrawUsage));
    }
    this.count = nextCount;
    if (this.field) this.setField(this.field, this.bed, this.depth, this.lakeId);
    else {
      this.object.geometry.setDrawRange(0, 0);
      this.dots.geometry.setDrawRange(0, 0);
    }
  }

  setPixelRatio(pixelRatio = 1) {
    this.dots.material.uniforms.uPixelRatio.value = pixelRatio;
  }

  setClippingPlanes(planes) {
    this.object.material.clippingPlanes = planes;
    this.dots.material.clippingPlanes = planes;
  }

  setPalette(name) {
    if (!FLOW_COLOR_STOPS[name] || name === this.paletteName) return;
    this.paletteName = name;
    this.palette = flowPalette(name);
    if (this.field) this.write();
  }

  setField(field, bed, depth, lakeId = 0) {
    const hadField = Boolean(this.field);
    this.field = field;
    this.bed = bed;
    this.depth = depth;
    this.lakeId = lakeId;
    this.wet = [];
    if (field) {
      for (let row = 0; row < field.rows; row += 1) {
        for (let column = 0; column < field.columns; column += 1) {
          if (Number.isFinite(field.u[row * field.columns + column])) this.wet.push([row, column]);
        }
      }
    }
    this.refreshSpawnCells();
    if (hadField && field) {
      for (let index = 0; index < this.count; index += 1) {
        const offset = index * this.trail * 2;
        const latitude = this.history[offset];
        const longitude = this.history[offset + 1];
        if (!Number.isFinite(latitude) || !this.isWater(latitude, longitude)
          || !currentAt(field, latitude, longitude)) this.spawn(index);
      }
      this.write();
    } else this.seedParticles();
  }

  setSpawnBounds(bounds) {
    this.spawnBounds = bounds;
    if (!this.field) return;
    this.refreshSpawnCells();
    const region = this.activeSpawnBounds;
    if (region) {
      for (let index = 0; index < this.count; index += 1) {
        const offset = index * this.trail * 2;
        const latitude = this.history[offset];
        const longitude = this.history[offset + 1];
        if (!Number.isFinite(latitude) || latitude < region.south || latitude > region.north
          || longitude < region.west || longitude > region.east) this.spawn(index);
      }
    }
    this.write();
  }

  refreshSpawnCells() {
    if (!this.spawnBounds || !this.wet.length || !this.field) {
      this.spawnWet = this.wet;
      this.activeSpawnBounds = null;
      this.updateAcceleration();
      return;
    }
    const { south, north, west, east } = this.spawnBounds;
    const centerLatitude = (south + north) / 2;
    const centerLongitude = (west + east) / 2;
    const halfLatitude = Math.max((north - south) / 2, this.field.cellLatitude);
    const halfLongitude = Math.max((east - west) / 2, this.field.cellLongitude);
    const targetCells = Math.min(48, this.wet.length);
    let visible = [];
    let activeBounds = this.spawnBounds;

    // Keep enough nearby water cells to spread particles across small bays,
    // even when the exact camera footprint contains only a few model cells.
    for (const scale of [1, 1.5, 2.25, 3.5, 5]) {
      activeBounds = {
        south: centerLatitude - halfLatitude * scale,
        north: centerLatitude + halfLatitude * scale,
        west: centerLongitude - halfLongitude * scale,
        east: centerLongitude + halfLongitude * scale
      };
      visible = this.wet.filter(([row, column]) => {
        const latitude = this.field.south + row * this.field.cellLatitude;
        const longitude = this.field.west + column * this.field.cellLongitude;
        return latitude >= activeBounds.south && latitude <= activeBounds.north
          && longitude >= activeBounds.west && longitude <= activeBounds.east
          && this.isWater(latitude, longitude);
      });
      if (visible.length >= targetCells) break;
    }
    if (visible.length) {
      this.spawnWet = visible;
      this.activeSpawnBounds = activeBounds;
      this.updateAcceleration(activeBounds);
    } else {
      this.spawnWet = this.wet;
      this.activeSpawnBounds = null;
      this.updateAcceleration();
    }
  }

  updateAcceleration(bounds = null) {
    if (!this.field) return;
    let extentMetres;
    if (bounds) {
      const centerLatitude = (bounds.south + bounds.north) / 2;
      extentMetres = Math.max(
        (bounds.north - bounds.south) * 111320,
        (bounds.east - bounds.west) * 111320 * Math.cos(centerLatitude * Math.PI / 180)
      );
    } else {
      extentMetres = Math.max(this.field.rows * this.field.cellLatitude, this.field.columns * this.field.cellLongitude * 0.7) * 111320;
    }
    // Keep 0.1 m/s currents moving about 2.5% of the active view each second.
    this.acceleration = (Math.max(1000, extentMetres) * 0.025) / 0.1;
  }

  seedParticles() {
    for (let index = 0; index < this.count; index += 1) this.spawn(index, true);
    // Run the flow briefly so trails are visible even while paused.
    if (this.field) for (let step = 0; step < this.trail; step += 1) this.step(RECORD_SECONDS);
    this.write();
  }

  spawn(index, scatterAge = false) {
    const offset = index * this.trail * 2;
    const cells = this.spawnWet.length ? this.spawnWet : this.wet;
    if (!cells.length) {
      this.history.fill(NaN, offset, offset + this.trail * 2);
      return;
    }
    for (let attempt = 0; attempt < 20; attempt += 1) {
      const [row, column] = cells[Math.floor(Math.random() * cells.length)];
      const latitude = this.field.south + (row + (Math.random() - 0.5) * 0.7) * this.field.cellLatitude;
      const longitude = this.field.west + (column + (Math.random() - 0.5) * 0.7) * this.field.cellLongitude;
      if (!this.isWater(latitude, longitude) || !currentAt(this.field, latitude, longitude)) continue;
      for (let step = 0; step < this.trail; step += 1) {
        this.history[offset + step * 2] = latitude;
        this.history[offset + step * 2 + 1] = longitude;
      }
      this.life[index] = 2.5 + Math.random() * 4;
      this.age[index] = scatterAge ? Math.random() * this.life[index] : 0;
      this.speed[index] = 0;
      return;
    }
    this.history.fill(NaN, offset, offset + this.trail * 2);
    this.age[index] = 0;
    this.life[index] = 0;
    this.speed[index] = 0;
  }

  // Advance by dt seconds of animation. The head moves every frame and a
  // trail point is kept every RECORD_SECONDS, so trails have a steady length.
  step(dt) {
    if (!this.field) return;
    const acceleration = this.acceleration;
    this.sinceRecord += dt;
    const record = this.sinceRecord >= RECORD_SECONDS;
    if (record) this.sinceRecord = 0;
    for (let index = 0; index < this.count; index += 1) {
      const offset = index * this.trail * 2;
      let latitude = this.history[offset];
      let longitude = this.history[offset + 1];
      this.age[index] += dt;
      const flow = Number.isFinite(latitude) ? currentAt(this.field, latitude, longitude) : null;
      if (!flow || this.age[index] > this.life[index]) {
        this.spawn(index);
        continue;
      }
      const [u, v] = flow;
      this.speed[index] = Math.hypot(u, v);
      const metres = acceleration * dt;
      latitude += (v * metres) / 111320;
      longitude += (u * metres) / (111320 * Math.cos(latitude * Math.PI / 180));
      if (!this.isWater(latitude, longitude)) {
        this.spawn(index);
        continue;
      }
      if (record) this.history.copyWithin(offset + 2, offset, offset + (this.trail - 1) * 2);
      this.history[offset] = latitude;
      this.history[offset + 1] = longitude;
    }
    this.write();
  }

  write() {
    // Just above the depth slice so the streaks never sink into it.
    const y = -(this.depth - 0.6) / 1000;
    const maximum = this.field?.scaleMaximum || 0.1;
    for (let index = 0; index < this.count; index += 1) {
      const offset = index * this.trail * 2;
      const fadeIn = Math.min(1, this.age[index] / 0.6);
      const fadeOut = Math.min(1, Math.max(0, (this.life[index] - this.age[index]) / 0.8));
      const relative = Math.min(1, this.speed[index] / maximum);
      const color = this.palette[Math.round(relative * 63)];
      const strength = 0.45 + 0.55 * relative;
      const headLatitude = this.history[offset];
      const headLongitude = this.history[offset + 1];
      const headValid = Number.isFinite(headLatitude) && Number.isFinite(headLongitude);
      const [headX, headZ] = headValid ? toWorld(headLatitude, headLongitude) : [0, 0];
      this.dotPositions.set([headX, y, headZ], index * 3);
      this.dotColors.set([color.r, color.g, color.b, headValid ? strength * fadeIn * fadeOut : 0], index * 4);
      for (let step = 0; step < this.trail - 1; step += 1) {
        const segment = (index * (this.trail - 1) + step) * 2;
        const lat0 = this.history[offset + step * 2];
        const lon0 = this.history[offset + step * 2 + 1];
        const lat1 = this.history[offset + step * 2 + 2];
        const lon1 = this.history[offset + step * 2 + 3];
        const valid = Number.isFinite(lat0) && Number.isFinite(lat1);
        const [x0, z0] = valid ? toWorld(lat0, lon0) : [0, 0];
        const [x1, z1] = valid ? toWorld(lat1, lon1) : [0, 0];
        const alpha0 = valid ? strength * fadeIn * fadeOut * (1 - step / this.trail) : 0;
        const alpha1 = valid ? strength * fadeIn * fadeOut * (1 - (step + 1) / this.trail) : 0;
        this.positions.set([x0, y, z0, x1, y, z1], segment * 3);
        this.colors.set([color.r, color.g, color.b, alpha0, color.r, color.g, color.b, alpha1], segment * 4);
      }
    }
    const geometry = this.object.geometry;
    geometry.setDrawRange(0, this.count * (this.trail - 1) * 2);
    geometry.attributes.position.needsUpdate = true;
    geometry.attributes.color.needsUpdate = true;
    const dotGeometry = this.dots.geometry;
    dotGeometry.setDrawRange(0, this.count);
    dotGeometry.attributes.position.needsUpdate = true;
    dotGeometry.attributes.color.needsUpdate = true;
  }

  dispose() {
    this.object.geometry.dispose();
    this.dots.geometry.dispose();
    this.object.material.dispose();
    this.dots.material.dispose();
  }

  isWater(latitude, longitude) {
    return bedDepthAt(this.bed, latitude, longitude) > this.depth + 0.5
      && (!this.lakeId || lakeIdAt(this.bed, latitude, longitude) === this.lakeId);
  }
}

// Each volume owns its GPU resources and lake mask; shared model grids must
// never paint a neighbouring lake (notably Michigan and Huron).
export class WaterLayer {
  constructor(volume, shared, relief, palette, particleCount = 2200) {
    this.volume = volume;
    this.currentField = null;
    this.isothermInfo = null;
    this.targetC = null;
    this.texture = volumeTexture(volume);
    this.uniforms = volumeUniforms();
    this.uniforms.uPalette.value = palette;
    applyVolumeUniforms(this.uniforms, volume, this.texture, volume.temperature.range);
    const masked = { ...shared, uLakeId: { value: LAKE_IDS[volume.lake] } };
    this.materials = {
      slice: sliceMaterial(masked, this.uniforms, relief),
      curtain: curtainMaterial(masked, this.uniforms),
      isotherm: isothermSurfaceMaterial(masked, this.uniforms)
    };
    const grid = volume.temperature;
    this.slice = new THREE.Mesh(groundPlane(grid.south - grid.cellLatitude / 2, grid.west - grid.cellLongitude / 2,
      grid.north + grid.cellLatitude / 2, grid.east + grid.cellLongitude / 2), this.materials.slice);
    const segments = Math.min(512, Math.max(grid.columns, grid.rows) * 2);
    this.isotherm = new THREE.Mesh(groundPlane(grid.south, grid.west, grid.north, grid.east, segments), this.materials.isotherm);
    this.isotherm.visible = false;
    this.curtain = null;
    this.particles = new FlowParticles(particleCount);
    this.particles.object.visible = false;
    this.particles.dots.visible = false;
    this.group = new THREE.Group();
    this.group.name = `${volume.lake}-water`;
    this.group.add(this.slice, this.isotherm, this.particles.object, this.particles.dots);
  }

  setSection(section, maxDepth) {
    if (this.curtain) {
      this.group.remove(this.curtain);
      this.curtain.geometry.dispose();
      this.curtain = null;
    }
    if (section) {
      this.curtain = new THREE.Mesh(curtainGeometry(...section, maxDepth), this.materials.curtain);
      this.group.add(this.curtain);
    }
  }

  dispose() {
    this.texture.dispose();
    this.materials.isotherm.uniforms.uSurface.value?.dispose();
    for (const mesh of [this.slice, this.isotherm, this.curtain]) mesh?.geometry.dispose();
    for (const material of Object.values(this.materials)) material.dispose();
    this.particles.dispose();
    this.group.clear();
  }
}
